import { match } from 'ts-pattern';
import { z } from 'zod';
import { shippingProtectionClaimStateSchema } from '~/stores/slices/shippingProtectionClaim';
import api from '~/utils/api';
import { GspReasonLabels } from '~/utils/constants';
import { toCurrency } from '~/utils/formatters';
import { logError } from '~/utils/observability';
import {
  makeShippingProtectionClaimAction,
  makeShippingProtectionClaimLoader,
  redirect,
} from '~/utils/routing';

const completeShippingClaim = z.object({
  reason: shippingProtectionClaimStateSchema.unwrap().shape.reason.unwrap(),
  itemIds: shippingProtectionClaimStateSchema.unwrap().shape.itemIds.min(1),
  meta: shippingProtectionClaimStateSchema.unwrap().shape.meta.unwrap(),
  address: shippingProtectionClaimStateSchema.unwrap().shape.address.unwrap(),
});

export const { loader, useLoaderData } = makeShippingProtectionClaimLoader(
  ({ context: { order, shippingProtectionClaim } }) =>
    match(completeShippingClaim.safeParse(shippingProtectionClaim))
      .with({ success: true }, ({ data: completedClaim }) => {
        const formatPrice = toCurrency(order.currencyCode);

        return {
          address: completedClaim.address,
          items: order.lineItems
            .filter((item) => completedClaim.itemIds.includes(item.id))
            .map((item) => ({
              ...item,
              unitDisplayPrice: formatPrice(item.unitPrice),
            })),
          reason: GspReasonLabels[completedClaim.reason],
          meta: completedClaim.meta,
        };
      })
      .with({ success: false }, ({ error }) => {
        logError(
          new Error(
            'Invalid shipping protection claim context at shipping claim review loader',
            { cause: error },
          ),
          { extra: { error } },
        );
        return redirect(`../${order.idFromPlatform}`);
      })
      .exhaustive(),
);

type Context = Parameters<
  Parameters<typeof makeShippingProtectionClaimAction>[0]
>[0]['context'];

const createProtectionClaim = (
  order: Context['order'],
  data: z.infer<typeof completeShippingClaim>,
) =>
  api.createProtectionClaim({
    params: { storefrontId: order.storefrontId },
    body: {
      reason: data.reason,
      originalStoreOrder: {
        id: order.id,
        lineItems: order.lineItems
          .filter((item) => data.itemIds?.includes(item.id))
          .map(({ id, quantity }) => ({
            id,
            quantity,
          })),
      },
      noteFromCustomer: data.meta?.noteFromCustomer,
      images: data.meta?.assets?.map(({ name }) => name) ?? [],
      shippingAddress: data.address,
    },
  });

export const { action, useActionResult: useReviewActionResult } =
  makeShippingProtectionClaimAction(
    async ({ context: { order, shippingProtectionClaim } }, appStore) =>
      match(completeShippingClaim.safeParse(shippingProtectionClaim))
        .with({ success: true }, ({ data }) =>
          createProtectionClaim(order, data).then((claim) => {
            appStore.dispatch(
              appStore.actions.completeShippingProtectionClaim(),
            );

            return redirect(`../../claim/${claim.id}/new`);
          }),
        )
        .with(
          { success: false },
          ({ error }) =>
            new Error('Invalid shipping claim action context', {
              cause: error,
            }),
        )
        .exhaustive(),
  );
